import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AppContext';
import { Home, User, LogOut, LogIn, Compass, Library, Flame, Shield, Mic2, Settings, Users, Film } from 'lucide-react';
import { NotificationBell } from './NotificationBell';

const API = process.env.REACT_APP_BACKEND_URL;

// Admin email whitelist
const ADMIN_EMAILS = ['modtest@aivue.com', 'admin@audivue.com', 'cpowersv@gmail.com'];

export const Sidebar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  
  const isAdmin = user && ADMIN_EMAILS.includes(user.email);
  const isArtist = user && user.role === 'artist';

  const navItems = [
    { icon: Home, label: 'Home', path: '/', requiresAuth: false },
    { icon: Flame, label: 'Pulse', path: '/pulse', requiresAuth: false },
    { icon: Film, label: 'Films', path: '/films', requiresAuth: false },
    // Discover is signed-in only (personalized rails / recommendation surfaces).
    // The Trending Now and Latest Uploads pages remain public via their own routes.
    ...(user ? [{ icon: Compass, label: 'Discover', path: '/discover', requiresAuth: true }] : []),
    { icon: Users, label: 'Artists', path: '/artists', requiresAuth: false },
    ...(user ? [{ icon: Library, label: 'Library', path: '/library', requiresAuth: true }] : []),
    // Iter 123: "Become a Creator" flow removed. Nav shows "My Studio" for
    // everyone signed in — backend auto-promotes role on first upload.
    ...(user ? [{ icon: Mic2, label: 'My Studio', path: '/studio', requiresAuth: true }] : []),
    // Settings lives inside My Studio for artists, but we keep it as a
    // top-level nav item for listeners (users who signed up but haven't
    // uploaded yet) so they still have a clear place to reach account
    // controls without ever seeing an "artist-only" surface.
    ...(user && !isArtist ? [{ icon: Settings, label: 'Settings', path: '/profile', requiresAuth: true }] : []),
    ...(isAdmin ? [{ icon: Shield, label: 'Admin', path: '/admin', requiresAuth: true, isAdmin: true }] : []),
  ];

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const avatarUrl = user?.avatar ? `${API}/api/files/${user.avatar}` : null;
  const displayName = user ? (user.display_name || user.username) : 'AUDIVUE';
  const initial = (user?.display_name || user?.username || 'A').charAt(0).toUpperCase();

  return (
    <aside className="hidden lg:flex flex-col w-64 h-screen bg-[#111111] border-r border-zinc-800 fixed left-0 top-0 z-40">
      {/* Brand header - user's profile pic fills space, eye stays as a corner badge */}
      <NavLink
        to={user ? '/profile' : '/'}
        className="block border-b border-zinc-800/50 group"
        aria-label={user ? 'Open profile' : 'AUDIVUE home'}
      >
        <div className="relative w-full aspect-square overflow-hidden">
          {/* Main image: avatar if logged in, eye if not */}
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt={displayName}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          ) : user ? (
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-orange-500/30 via-purple-500/20 to-zinc-900">
              <span className="text-7xl font-bold text-white/90">{initial}</span>
            </div>
          ) : (
            <img
              src="/sidebar-logo.png"
              alt="AUDIVUE"
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
          )}
          {/* Bottom gradient + name */}
          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent px-3 pt-8 pb-3">
            <p className="text-sm font-semibold text-white truncate" data-testid="sidebar-display-name">
              {displayName}
            </p>
            {user ? (
              <p className="text-[11px] uppercase tracking-wider text-orange-400">{isArtist ? 'Artist' : 'Listener'}</p>
            ) : (
              <p className="text-[11px] uppercase tracking-wider text-zinc-500">Music videos, reborn</p>
            )}
          </div>
          {/* Eye badge in top-right corner — keeps brand visible even when avatar is shown */}
          {user && (
            <div
              className="absolute top-2 right-2 w-9 h-9 rounded-xl bg-black/60 backdrop-blur-md border border-white/10 p-1 flex items-center justify-center"
              title="AUDIVUE"
            >
              <img src="/sidebar-logo.png" alt="AUDIVUE" className="w-full h-full rounded-lg" />
            </div>
          )}
        </div>
      </NavLink>

      {/* Navigation */}
      <nav className="flex-1 px-3 py-4">
        <ul className="space-y-1">
          {navItems.map((item) => {
            if (item.requiresAuth && !user) return null;
            
            return (
              <li key={item.path}>
                <NavLink
                  to={item.path}
                  className={({ isActive }) =>
                    `flex items-center gap-3 ${item.nested ? 'pl-10 pr-4' : 'px-4'} py-3 rounded-xl transition-all duration-200 ${
                      isActive
                        ? 'bg-white/10 text-white'
                        : 'text-zinc-400 hover:text-white hover:bg-zinc-800/50'
                    }`
                  }
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <item.icon className={`${item.nested ? 'w-4 h-4' : 'w-5 h-5'}`} />
                  <span className={`font-medium ${item.nested ? 'text-sm' : ''}`}>{item.label}</span>
                </NavLink>
              </li>
            );
          })}
          {user && (
            <li>
              <NotificationBell variant="sidebar" />
            </li>
          )}
        </ul>
      </nav>

      {/* Bottom section */}
      <div className="px-3 pb-4 border-t border-zinc-800/50 pt-3">
        {user ? (
          <>
            <NavLink
              to="/profile"
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 ${
                  isActive
                    ? 'bg-white/10 text-white'
                    : 'text-zinc-400 hover:text-white hover:bg-zinc-800/50'
                }`
              }
              data-testid="nav-profile"
            >
              <User className="w-5 h-5" />
              <span className="font-medium truncate">{user.display_name || user.username}</span>
            </NavLink>
            <button
              onClick={handleLogout}
              className="flex items-center gap-3 px-4 py-3 rounded-xl text-zinc-500 hover:text-red-400 hover:bg-red-500/10 transition-all duration-200 w-full"
              data-testid="nav-logout"
            >
              <LogOut className="w-5 h-5" />
              <span className="font-medium">Sign Out</span>
            </button>
          </>
        ) : (
          <div className="space-y-2">
            <NavLink
              to="/register"
              className="flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-white text-black font-semibold hover:bg-zinc-200 transition-colors"
              data-testid="nav-signup"
            >
              Sign Up Free
            </NavLink>
            <NavLink
              to="/login"
              className="flex items-center justify-center gap-2 px-4 py-3 rounded-full border border-zinc-700 text-white font-medium hover:bg-zinc-800 transition-colors"
              data-testid="nav-login"
            >
              <LogIn className="w-4 h-4" />
              Log In
            </NavLink>
          </div>
        )}
      </div>
    </aside>
  );
};

// Mobile bottom nav — clean icons with pulse fire effect
export const BottomNav = () => {
  const { user } = useAuth();
  const isArtist = user && user.role === 'artist';

  const navItems = [
    { icon: Home, label: 'Home', path: '/' },
    { icon: Flame, label: 'Pulse', path: '/pulse' },
    ...(user ? [{ icon: Library, label: 'Library', path: '/library' }] : []),
    ...(user ? [{ icon: Mic2, label: 'Studio', path: '/studio' }] : []),
    // Artists open Studio's Settings tab; listeners go to /profile; anon -> /login.
    user
      ? (isArtist
          ? { icon: User, label: 'Account', path: '/studio?tab=settings' }
          : { icon: User, label: 'Profile', path: '/profile' })
      : { icon: LogIn, label: 'Sign In', path: '/login' },
  ];

  return (
    <nav
      className="lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-black/95 backdrop-blur-md border-t border-[#222]"
      style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
    >
      <ul className="flex items-center justify-around py-3">
        {navItems.map((item) => (
          <li key={item.path + item.label}>
            <NavLink
              to={item.path}
              className={({ isActive }) =>
                `relative flex flex-col items-center gap-1 px-5 py-2 transition-all duration-200 ${
                  isActive ? 'text-orange-400' : 'text-[#666] hover:text-white'
                }`
              }
              data-testid={`mobile-nav-${item.label.toLowerCase()}`}
            >
              {({ isActive }) => (
                <>
                  {/* Pulse fire glow effect for active item */}
                  {isActive && (
                    <span className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <span className="w-10 h-10 rounded-full bg-orange-500/30 animate-pulse-fire" />
                    </span>
                  )}
                  <item.icon className={`relative z-10 w-6 h-6 ${item.label === 'Upload' ? 'stroke-[2.5]' : ''} ${isActive ? 'drop-shadow-[0_0_8px_rgba(251,146,60,0.8)]' : ''}`} />
                  <span className={`relative z-10 text-[10px] font-medium ${isActive ? 'text-orange-400' : ''}`}>{item.label}</span>
                </>
              )}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
};
